import React, { useMemo } from 'react';
import { 
  faFileExport, 
  faTrash, 
  faUserPlus,
  faRefresh
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS } from '../../utils/colors';
import ActionToolbar from '../common/ActionToolbar';
import { useTranslation } from 'react-i18next';

/**
 * UserActions Component
 * Action buttons for user management (bulk actions, export, etc.)
 */
const UserActions = ({ 
  selectedUsers = [], 
  onAddUser, 
  onDeleteSelected, 
  onRefresh,
  showSearch = true,
  searchValue = '',
  onSearchChange,
  loading = false,
  refreshLoading = false,
  users = [],
  onExportCSV,
  is_active,
  onActiveFilterChange
}) => {
  const { t } = useTranslation();
  const toolbarFilters = useMemo(() => {
    return [
      {
        key: 'is_active_filter',
        type: 'select',
        label: t('commonCols.status') + ':',
        value: is_active === null || is_active === undefined ? 'all' : String(is_active),
        options: [
          { value: 'true', label: t('commonCols.statusOptions.active') },
          { value: 'false', label: t('commonCols.statusOptions.inactive') },
          { value: 'all', label: t('commonCols.statusOptions.allUsers') }
        ],
        onChange: (val) => {
          let mappedVal = val;
          if (val === 'true') mappedVal = true;
          else if (val === 'false') mappedVal = false;
          else mappedVal = null;
          onActiveFilterChange && onActiveFilterChange(mappedVal);
        }
      }
    ];
  }, [is_active, onActiveFilterChange]);

  /**
   * Build the toolbar actions array.
   * Returns action descriptors consumed by `ActionToolbar` (icon, label, handler, state).
   */
  const actions = useMemo(() => {
    const items = [];

    if (selectedUsers.length > 0) {
      items.push({
        key: 'delete-selected',
  label: t('common.buttons.deleteSelected'),
        icon: faTrash,
        onClick: onDeleteSelected,
        disabled: loading,
        variant: 'danger',
        showLabelBreakpoint: 'sm',
      });
    }

    items.push(
      {
        key: 'export-users',
  label: t('common.buttons.exportCsv'),
        icon: faFileExport,
        onClick: () => onExportCSV && onExportCSV({ format: 'csv' }),
        disabled: loading,
        variant: 'secondary',
        showLabelBreakpoint: 'sm',
        textColor: UI_COLORS.TEXT.WHITE,
      },
      {
        key: 'refresh-users',
  label: t('common.buttons.refresh'),
        icon: faRefresh,
        onClick: onRefresh,
        disabled: refreshLoading,
        variant: 'ghost',
        showLabelBreakpoint: 'sm',
        spinner: refreshLoading,
      },
      {
        key: 'add-user',
  label: t('users.actions.buttons.add'),
        icon: faUserPlus,
        onClick: onAddUser,
        disabled: loading,
        variant: 'primary',
        showLabelBreakpoint: 'sm',
        loading,
      }
    );

    return items;
  }, [onExportCSV, loading, onAddUser, onDeleteSelected, onRefresh, refreshLoading, selectedUsers.length, t]);

  return (
    <ActionToolbar
      showSearch={showSearch}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={t('users.actions.searchPlaceholder')}
      actions={actions}
      filters={toolbarFilters}
    />
  );
};

export default UserActions;